/**
 * Remembers where the user was when the session ended (or when an
 * unauthenticated visit was redirected to /login) so the login flow can send
 * them back there once it completes. Kept in sessionStorage: per tab, gone
 * when the tab closes.
 */
const RETURN_KEY = "nabble.returnTo";
const EXPIRED_KEY = "nabble.sessionExpired";

const AUTH_PATHS = ["/login", "/forgot-password", "/invite", "/reset-password"];

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Only same-app absolute paths, never auth pages or protocol-relative URLs. */
export function isSafeReturnPath(path: string): boolean {
  if (!path.startsWith("/") || path.startsWith("//") || path.includes("\\")) return false;
  const pathname = path.split(/[?#]/)[0] ?? "";
  return !AUTH_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function rememberReturnTo(path: string): void {
  if (!isSafeReturnPath(path) || path === "/") return;
  try {
    storage()?.setItem(RETURN_KEY, path);
  } catch {
    // ignore: storage unavailable
  }
}

/** Reads the remembered path without clearing it. */
export function peekReturnTo(): string | null {
  try {
    const value = storage()?.getItem(RETURN_KEY) ?? null;
    return value && isSafeReturnPath(value) ? value : null;
  } catch {
    return null;
  }
}

/** Reads and clears the remembered path. */
export function takeReturnTo(): string | null {
  const s = storage();
  try {
    const value = s?.getItem(RETURN_KEY) ?? null;
    s?.removeItem(RETURN_KEY);
    return value && isSafeReturnPath(value) ? value : null;
  } catch {
    return null;
  }
}

export function clearReturnTo(): void {
  try {
    storage()?.removeItem(RETURN_KEY);
  } catch {
    // ignore
  }
}

export function markSessionExpired(): void {
  try {
    storage()?.setItem(EXPIRED_KEY, "1");
  } catch {
    // ignore
  }
}

export function wasSessionExpired(): boolean {
  try {
    return storage()?.getItem(EXPIRED_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearSessionExpired(): void {
  try {
    storage()?.removeItem(EXPIRED_KEY);
  } catch {
    // ignore
  }
}
